import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui';
import { useAppDispatch } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';
import { courrierService } from '../services/api';
import { queryKeys } from '../lib/react-query';
import { User } from '../types/api';
import { useUsers } from '../hooks/useUsers';

interface CourrierAssignmentProps {
  courrierId: number;
  currentAssignedTo?: number;
  onAssignment?: (assignedTo?: number) => void;
  disabled?: boolean;
}

export function CourrierAssignment({
  courrierId,
  currentAssignedTo,
  onAssignment,
  disabled = false,
}: CourrierAssignmentProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data: users, isLoading: usersLoading } = useUsers();

  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [comment, setComment] = useState('');
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);

  // Mutation pour l'assignation
  const assignmentMutation = useMutation({
    mutationFn: ({ assignedTo, comment }: { assignedTo: number; comment?: string }) =>
      courrierService.assignCourrier(courrierId, { assignedTo, comment }),
    onSuccess: (updatedCourrier) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(courrierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.statistics() });

      // Notification de succès
      dispatch(addNotification({
        type: 'success',
        title: 'Courrier assigné',
        message: `Le courrier a été assigné avec succès`,
      }));

      // Callback
      onAssignment?.(updatedCourrier.assignedTo);

      // Réinitialiser le formulaire
      setSelectedUserId('');
      setComment('');
      setShowAssignmentForm(false);
    },
    onError: (error: any) => {
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de l\'assignation',
      }));
    },
  });

  const currentUser = users?.find(user => user.id === currentAssignedTo);
  const availableUsers = users?.filter(user => user.id !== currentAssignedTo) || [];

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(parseInt(userId));
    if (!showAssignmentForm) {
      setShowAssignmentForm(true);
    }
  };

  const handleAssignment = () => {
    if (!selectedUserId) return;

    assignmentMutation.mutate({
      assignedTo: selectedUserId,
      comment: comment.trim() || undefined,
    });
  };

  const handleCancel = () => {
    setSelectedUserId('');
    setComment('');
    setShowAssignmentForm(false);
  };

  const handleUnassign = () => {
    assignmentMutation.mutate({
      assignedTo: 0, // 0 signifie désassignation
      comment: 'Désassignation automatique',
    });
  };

  return (
    <div className="space-y-4">
      {/* Utilisateur actuellement assigné */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-gray-700">Assigné à :</span>
          {currentUser ? (
            <div className="mt-1 flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {currentUser.fullName}
              </span>
              <span className="text-xs text-gray-500">({currentUser.email})</span>
            </div>
          ) : (
            <span className="text-sm text-gray-500 ml-2">Non assigné</span>
          )}
        </div>

        <div className="flex space-x-2">
          {currentUser && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnassign}
              disabled={disabled || assignmentMutation.isPending}
            >
              Désassigner
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAssignmentForm(!showAssignmentForm)}
            disabled={disabled}
          >
            {showAssignmentForm ? 'Annuler' : 'Assigner'}
          </Button>
        </div>
      </div>

      {/* Formulaire d'assignation */}
      {showAssignmentForm && (
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un utilisateur
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => handleUserSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={assignmentMutation.isPending || usersLoading}
            >
              <option value="">Choisir un utilisateur...</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.fullName} ({user.email}) - {user.role}
                </option>
              ))}
            </select>
          </div>

          {selectedUserId && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajouter un commentaire pour expliquer cette assignation..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  disabled={assignmentMutation.isPending}
                />
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={handleAssignment}
                  isLoading={assignmentMutation.isPending}
                  disabled={assignmentMutation.isPending}
                >
                  Confirmer l'assignation
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={assignmentMutation.isPending}
                >
                  Annuler
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Composant pour afficher l'utilisateur assigné (version compacte)
interface AssignedUserDisplayProps {
  user?: User;
  showRole?: boolean;
  showEmail?: boolean;
}

export function AssignedUserDisplay({ user, showRole = false, showEmail = false }: AssignedUserDisplayProps) {
  if (!user) {
    return <span className="text-gray-400 text-sm">Non assigné</span>;
  }

  return (
    <div className="flex items-center space-x-2">
      <span className="font-medium text-gray-900">{user.fullName}</span>
      {showEmail && (
        <span className="text-gray-500 text-sm">({user.email})</span>
      )}
      {showRole && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          {user.role}
        </span>
      )}
    </div>
  );
}